import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  limit,
  getDocs,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';
import { logAdminActivity } from '../services/adminActivityLog';
import { useToast } from './ui/Toast';
import Badge from './ui/Badge';
import { Send, Loader2, Trash2 } from 'lucide-react';

/**
 * Reusable internal-notes panel. Works for any parent document by
 * passing `parentSegments` (the Firestore path to the parent), e.g.
 *   ['users', userId]  → users/{userId}/admin_notes
 *   ['orders', orderId] → orders/{orderId}/admin_notes
 *
 * All staff (admin / admin_master / operator) can add and delete notes
 * — the Firestore rules enforce the same. Notes are append-only (no
 * editing), so the only actions are create + delete.
 */
export default function AdminNotes({ parentSegments, targetType, targetLabel }) {
  const { user: currentAdmin } = useAuth();
  const { toast } = useToast();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const notesCollection = useCallback(
    () => collection(db, ...parentSegments, 'admin_notes'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(parentSegments)],
  );

  const fetchNotes = useCallback(async () => {
    try {
      setLoading(true);
      const q = query(notesCollection(), orderBy('createdAt', 'desc'), limit(100));
      const snap = await getDocs(q);
      setNotes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('notes load:', e);
    } finally {
      setLoading(false);
    }
  }, [notesCollection]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (!currentAdmin?.uid) return;
    try {
      setSubmitting(true);
      await addDoc(notesCollection(), {
        text: trimmed,
        authorId: currentAdmin.uid,
        authorName: currentAdmin.name || null,
        authorEmail: currentAdmin.email || null,
        authorType: currentAdmin.type || null,
        createdAt: serverTimestamp(),
      });
      logAdminActivity({
        actor: currentAdmin,
        action: 'add_note',
        targetType: targetType || parentSegments[0],
        targetId: parentSegments[1] || null,
        targetLabel: targetLabel || null,
      });
      setText('');
      await fetchNotes();
      toast.success('Note saved');
    } catch (e) {
      console.error(e);
      toast.error(`Failed: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (noteId) => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    try {
      setDeletingId(noteId);
      await deleteDoc(doc(db, ...parentSegments, 'admin_notes', noteId));
      logAdminActivity({
        actor: currentAdmin,
        action: 'delete_note',
        targetType: targetType || parentSegments[0],
        targetId: parentSegments[1] || null,
        targetLabel: targetLabel || null,
      });
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      toast.success('Note deleted');
    } catch (e) {
      console.error(e);
      toast.error(`Failed to delete: ${e.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const fmtDate = (ts) => {
    const d = ts?.toDate?.() || (ts ? new Date(ts) : null);
    return d ? d.toLocaleString('en-GB') : '—';
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">New note</label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          disabled={submitting}
          placeholder="Internal note — only staff can see this."
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
        />
        <div className="flex justify-end mt-2">
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !text.trim()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
            {submitting ? 'Saving…' : 'Add note'}
          </button>
        </div>
      </div>

      <div className="border-t border-slate-200 pt-3">
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading notes…
          </div>
        ) : notes.length === 0 ? (
          <p className="text-slate-500 text-sm">No notes yet.</p>
        ) : (
          <div className="space-y-3">
            {notes.map((n) => (
              <div key={n.id} className="border border-slate-200 rounded-lg p-3 bg-amber-50/40">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm text-slate-800 whitespace-pre-wrap flex-1">{n.text}</p>
                  <button
                    type="button"
                    onClick={() => remove(n.id)}
                    disabled={deletingId === n.id}
                    title="Delete note"
                    className="shrink-0 text-slate-400 hover:text-red-600 disabled:opacity-50 p-1"
                  >
                    {deletingId === n.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <div className="mt-2 flex items-center justify-between gap-2 text-xs text-slate-500">
                  <span>
                    <span className="font-semibold text-slate-700">{n.authorName || n.authorEmail || 'Admin'}</span>
                    {n.authorType && <Badge variant="secondary" className="ml-1.5">{n.authorType}</Badge>}
                  </span>
                  <span>{fmtDate(n.createdAt)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
